import React from 'react';
import LoadingSpinner from './LoadingSpinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonVariantStyle = 'filled' | 'outlined' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Visual variant: primary (brand), secondary (emphasis), tertiary (muted) */
  variant?: ButtonVariant;
  /** Filled (solid), outlined (border), ghost (transparent) */
  variantStyle?: ButtonVariantStyle;
  /** Size of the button */
  size?: ButtonSize;
  /** Disabled state */
  disabled?: boolean;
  /** Loading state: shows spinner and disables interaction */
  loading?: boolean;
  /** Icon to show on the left of the label */
  iconLeft?: React.ReactNode;
  /** Icon to show on the right of the label */
  iconRight?: React.ReactNode;
  /** Stretch to full width of container */
  fullWidth?: boolean;
  /** Button content */
  children?: React.ReactNode;
  /** Optional class name for one-off overrides */
  className?: string;
  /** When true, no variant/size/ring/opacity styles are applied; use className and style for exact look (no styling change from original) */
  unstyled?: boolean;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-lg',
  md: 'px-4 py-2.5 text-base rounded-xl',
  lg: 'px-6 py-3 text-base rounded-xl',
};

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-2 font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 touch-manipulation';

function getVariantClasses(
  variant: ButtonVariant,
  variantStyle: ButtonVariantStyle
): string {
  if (variantStyle === 'outlined') {
    switch (variant) {
      case 'primary':
        return 'border-2 border-primary-500 text-primary-600 bg-transparent hover:bg-primary-50 focus-visible:ring-primary-500 disabled:border-gray-300 disabled:text-gray-500';
      case 'secondary':
        return 'border-2 border-secondary-500 text-secondary-600 bg-transparent hover:bg-secondary-50 focus-visible:ring-secondary-500 disabled:border-gray-300 disabled:text-gray-500';
      case 'tertiary':
        return 'border border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50 focus-visible:ring-gray-400 disabled:border-gray-200 disabled:text-gray-400';
      default:
        return '';
    }
  }
  if (variantStyle === 'ghost') {
    switch (variant) {
      case 'primary':
        return 'text-primary-600 bg-transparent hover:bg-primary-50 focus-visible:ring-primary-500 disabled:text-gray-500';
      case 'secondary':
        return 'text-secondary-600 bg-transparent hover:bg-secondary-50 focus-visible:ring-secondary-500 disabled:text-gray-500';
      case 'tertiary':
        return 'text-gray-700 bg-transparent hover:bg-gray-100 focus-visible:ring-gray-400 disabled:text-gray-400';
      default:
        return '';
    }
  }
  // filled (default)
  switch (variant) {
    case 'primary':
      return 'bg-primary-500 text-white hover:bg-primary-600 hover:scale-[1.02] active:scale-[0.98] focus-visible:ring-primary-500 disabled:bg-gray-400 disabled:hover:scale-100';
    case 'secondary':
      return 'bg-secondary-500 text-white hover:bg-secondary-600 hover:scale-[1.02] active:scale-[0.98] focus-visible:ring-secondary-500 disabled:bg-gray-400 disabled:hover:scale-100';
    case 'tertiary':
      return 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus-visible:ring-gray-400 disabled:bg-gray-100 disabled:text-gray-500';
    default:
      return '';
  }
}

const UNSTYLED_BASE = 'inline-flex items-center justify-center gap-2 focus:outline-none disabled:cursor-not-allowed touch-manipulation';

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  variantStyle = 'filled',
  size = 'md',
  disabled = false,
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  type = 'button',
  className = '',
  unstyled = false,
  children,
  onClick,
  ...rest
}, ref) => {
  const isDisabled = disabled || loading;
  const variantClasses = unstyled ? '' : getVariantClasses(variant, variantStyle);
  const sizeClasses = unstyled ? '' : SIZE_CLASSES[size];
  const widthClass = fullWidth ? 'w-full' : '';
  const baseClasses = unstyled ? UNSTYLED_BASE : BASE_CLASSES;

  const spinnerColor = variantStyle === 'filled' && (variant === 'primary' || variant === 'secondary') ? 'white' : 'gray';

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className={`${baseClasses} ${sizeClasses} ${variantClasses} ${widthClass} ${className}`.trim()}
      aria-busy={loading}
      aria-disabled={isDisabled}
      {...rest}
    >
      {loading ? (
        <>
          <LoadingSpinner size={size === 'lg' ? 'md' : size === 'sm' ? 'sm' : 'md'} color={spinnerColor} />
          <span>{children ?? 'Loading...'}</span>
        </>
      ) : (
        <>
          {iconLeft && <span className="inline-flex shrink-0" aria-hidden>{iconLeft}</span>}
          {children != null && (unstyled ? children : <span>{children}</span>)}
          {iconRight && <span className="inline-flex shrink-0" aria-hidden>{iconRight}</span>}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
